import { useEffect, useRef } from "react";

export interface UseEventListenerOptions extends AddEventListenerOptions {
  /** When `false`, no listener is attached and the handler never fires. Defaults to `true`. */
  enabled?: boolean;
}

/**
 * Subscribes to a DOM event on `window` for the lifetime of the calling component.
 *
 * Use this when an event must be caught regardless of what currently has focus — a global keyboard
 * shortcut, an `online`/`offline` change, a `resize`. For an event that originates inside your own
 * markup, prefer a plain React `onX` prop; it is simpler and scoped to the element.
 *
 * The handler is held in a ref, so an inline arrow function is fine: the listener is attached once
 * and always calls the latest render's handler, without re-subscribing or capturing stale props.
 *
 * @example
 * ```tsx
 * useEventListener("keydown", (e) => {
 *   if (e.key === "Escape") close();
 * });
 *
 * // Conditionally, and on the capture phase:
 * useEventListener("keydown", handleKey, { enabled: isOpen, capture: true });
 * ```
 */
export const useEventListener = <K extends keyof WindowEventMap>(
  type: K,
  handler: (event: WindowEventMap[K]) => void,
  { enabled = true, ...listenerOptions }: UseEventListenerOptions = {}
) => {
  const handlerRef = useRef(handler);
  useEffect(() => {
    handlerRef.current = handler;
  }, [handler]);

  const { capture, once, passive } = listenerOptions;

  useEffect(() => {
    if (!enabled) return;

    const listener = (event: WindowEventMap[K]) => handlerRef.current(event);
    const options = { capture, once, passive };

    window.addEventListener(type, listener, options);
    return () => window.removeEventListener(type, listener, options);
  }, [type, enabled, capture, once, passive]);
};
